import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, CheckCircle2, Play, Headphones, Download, ShieldCheck, Sparkles,
  Calculator, FileText, ListChecks, ChevronRight, Award, Building2, HardHat,
  Users, Briefcase, GraduationCap, Search, ChevronDown, Wrench, Clock,
  BookOpen, ClipboardCheck,
} from "lucide-react";
import QstLayout, { AiSafetyBadge } from "./QstLayout";
import QstAssistantWidget from "./QstAssistantWidget";
import { qstTrades, qstStats, qstLessons } from "./api";

const CONSTRUCTION_HERO = "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=1600&auto=format";

export default function QstHomePage() {
  const [trades, setTrades] = useState({ live: [], coming_soon: [] });
  const [stats, setStats] = useState(null);
  const [featured, setFeatured] = useState(null);
  const [selectedTrade, setSelectedTrade] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [t, s, l] = await Promise.all([
          qstTrades(),
          qstStats(),
          qstLessons({ free_only: true, limit: 8 }),
        ]);
        setTrades(t);
        setStats(s);
        setFeatured((l.items || []).find((x) => x.featured) || (l.items || [])[0]);
      } catch (e) {
        // Silent — page still renders with graceful defaults
      }
    })();
  }, []);

  useEffect(() => {
    document.title = "Quality Safety Times — Reduce Rework. Prevent Injuries. Build Better.";
  }, []);

  return (
    <QstLayout>
      <QstAssistantWidget context={{ page: "home" }} />

      {/* ============ HERO ============ */}
      <section className="relative bg-white overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-[0.04]" style={{
          backgroundImage: "repeating-linear-gradient(45deg, #0B2545 0 2px, transparent 2px 12px)"
        }} />
        <div className="relative max-w-7xl mx-auto px-6 pt-12 pb-16 grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-700 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-widest">
              <ShieldCheck className="w-3.5 h-3.5 text-[#F97316]" /> Expert-Authored · Field-Ready
            </div>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-black text-[#0B2545] tracking-tight leading-[1.02]">
              Reduce Rework.<br />Prevent Injuries.<br />
              <span className="text-[#F97316]">Build Better.</span>
            </h1>
            <p className="mt-5 text-lg text-slate-700 max-w-xl leading-relaxed">
              Expert-authored, trade-specific quality and safety training for construction crews.
              Give foremen short, practical lessons they can use with their crews — on the jobsite, in their language.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/qst/library?free_only=1"
                className="inline-flex items-center gap-2 rounded-md bg-[#F97316] hover:bg-[#EA580C] text-white px-6 py-3 font-bold shadow-md transition"
                data-testid="qst-hero-explore-free">
                Explore Free Training <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/qst/contact?intent=demo"
                className="inline-flex items-center gap-2 rounded-md border-2 border-[#0B2545] text-[#0B2545] hover:bg-[#0B2545] hover:text-white px-6 py-3 font-bold transition"
                data-testid="qst-hero-book-demo">
                Book a Demo
              </Link>
            </div>

            {/* Quick lesson finder */}
            <div className="mt-6 flex flex-col sm:flex-row gap-2 max-w-xl">
              <select value={selectedTrade} onChange={(e) => setSelectedTrade(e.target.value)}
                className="flex-1 rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm font-medium"
                data-testid="qst-hero-select-trade">
                <option value="">Select Your Trade</option>
                {trades.live.map((t) => (
                  <option key={t.slug} value={t.slug}>{t.name}</option>
                ))}
              </select>
              <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}
                className="flex-1 rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm font-medium"
                data-testid="qst-hero-select-topic">
                <option value="">Choose a Topic</option>
                <option value="quality-control">Quality Control</option>
                <option value="safety">Safety</option>
                <option value="loss-prevention">Loss Prevention</option>
              </select>
              <Link to={`/qst/library?${selectedTrade ? `trade=${selectedTrade}` : ""}${selectedCategory ? `&category=${selectedCategory}` : ""}`}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-[#0B2545] hover:bg-[#0F3B7D] text-white px-5 py-2.5 text-sm font-bold whitespace-nowrap"
                data-testid="qst-hero-find-training">
                Find Training <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* Right hero visual */}
          <div className="relative">
            <div className="rounded-2xl overflow-hidden shadow-2xl border border-slate-200">
              <img src={CONSTRUCTION_HERO} alt="Construction crew on jobsite receiving training"
                   className="w-full h-[420px] object-cover" />
            </div>
            <div className="absolute top-4 right-4 bg-white rounded-full pl-2 pr-3 py-1.5 shadow-lg flex items-center gap-1.5">
              <div className="w-5 h-5 rounded-full bg-[#0B2545] grid place-items-center">
                <CheckCircle2 className="w-3.5 h-3.5 text-white" />
              </div>
              <span className="text-[11px] font-bold text-slate-800">Expert-Authored · Field-Ready</span>
            </div>
          </div>
        </div>
      </section>

      {/* ============ THREE OUTCOMES ============ */}
      <section className="max-w-7xl mx-auto px-6 pt-4 pb-16">
        <h2 className="text-2xl sm:text-3xl font-bold text-[#0B2545]">Training Built for the Jobsite</h2>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { icon: Wrench, title: "Reduce Rework", desc: "Build quality into every task and avoid costly mistakes.", accent: "#F97316" },
            { icon: HardHat, title: "Strengthen Safety Culture", desc: "Give crews the knowledge to work safer, every day.", accent: "#0B2545" },
            { icon: Users, title: "Preserve Field Knowledge", desc: "Capture experience and pass it on to the next generation.", accent: "#059669" },
          ].map((o) => (
            <div key={o.title} className="rounded-xl border border-slate-200 bg-white p-6 hover:shadow-md transition" data-testid={`qst-outcome-${o.title.split(' ')[0].toLowerCase()}`}>
              <div className="w-11 h-11 rounded-lg grid place-items-center" style={{ background: `${o.accent}18` }}>
                <o.icon className="w-6 h-6" style={{ color: o.accent }} />
              </div>
              <div className="mt-4 text-lg font-bold text-[#0B2545]">{o.title}</div>
              <div className="mt-1.5 text-sm text-slate-700 leading-relaxed">{o.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ============ BROWSE BY TRADE ============ */}
      <section className="bg-slate-50 py-16" data-testid="qst-browse-trades">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0B2545]">Find the Right Lesson for Every Trade</h2>
            <Link to="/qst/trades" className="text-sm font-bold text-[#F97316] hover:underline">
              View all trades →
            </Link>
          </div>
          <div className="mt-6 flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-4 py-3 max-w-2xl">
            <Search className="w-4 h-4 text-slate-500" />
            <input placeholder="Search training topics, keywords, or trades…"
              className="flex-1 outline-none text-sm bg-transparent"
              data-testid="qst-trade-search-input" />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {["All Trades", "Safety", "Quality Control", "Tools & Equipment", "Installation", "Inspection", "More"].map((f, i) => (
              <button key={f}
                className={`rounded-full px-4 py-1.5 text-xs font-bold border transition ${i === 0 ? "bg-[#0B2545] text-white border-[#0B2545]" : "border-slate-300 text-slate-700 hover:bg-white"}`}>
                {f}
              </button>
            ))}
          </div>

          <div className="mt-7 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {trades.live.slice(0, 6).map((t) => (
              <Link key={t.slug} to={`/qst/trades/${t.slug}`}
                className="group rounded-xl overflow-hidden bg-white border border-slate-200 hover:shadow-xl hover:-translate-y-0.5 transition-all"
                data-testid={`qst-trade-card-${t.slug}`}>
                <div className="h-28 bg-slate-200 overflow-hidden">
                  {t.hero && <img src={t.hero} alt={t.name} className="w-full h-full object-cover" />}
                </div>
                <div className="p-3.5">
                  <div className="text-sm font-bold text-[#0B2545] line-clamp-1">{t.name}</div>
                  <div className="mt-0.5 text-[11px] text-slate-500 font-semibold">
                    {t.lesson_count}+ Lessons
                  </div>
                  <div className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-[#F97316] group-hover:underline">
                    Explore <ChevronRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <h2 className="text-2xl sm:text-3xl font-bold text-[#0B2545]">How QST Works</h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4 relative">
          {[
            { n: 1, icon: ListChecks, title: "Choose a Trade", desc: "Find training for your specific trade or task." },
            { n: 2, icon: Play, title: "Present the Lesson", desc: "Use on any device, at the jobsite or in the trailer." },
            { n: 3, icon: Users, title: "Engage the Crew", desc: "Spark discussion and apply it to real work." },
            { n: 4, icon: ClipboardCheck, title: "Document Training", desc: "Keep a record and show completion." },
          ].map((s) => (
            <div key={s.n} className="relative bg-white" data-testid={`qst-how-step-${s.n}`}>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-[#0B2545] text-white grid place-items-center font-black flex-shrink-0">
                  {s.n}
                </div>
                <div>
                  <div className="flex items-center gap-2 text-base font-bold text-[#0B2545]">
                    <s.icon className="w-4 h-4 text-[#F97316]" /> {s.title}
                  </div>
                  <div className="mt-1 text-sm text-slate-600 leading-relaxed">{s.desc}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ============ FEATURED TOOLBOX TALK ============ */}
      {featured && (
        <section className="bg-slate-50 py-16" data-testid="qst-featured-toolbox">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0B2545]">Featured Toolbox Talk</h2>
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-6 items-start">
              <div className="rounded-xl bg-white border border-slate-200 shadow-sm p-6">
                <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-5">
                  <div className="rounded-lg overflow-hidden bg-slate-200 aspect-[3/4] hidden sm:block">
                    <div className="w-full h-full bg-gradient-to-b from-[#0B2545] to-[#1D4ED8] grid place-items-center text-white">
                      <BookOpen className="w-12 h-12 opacity-60" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#0B2545]">{featured.title}</h3>
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                      <span className="inline-flex items-center gap-1 rounded-md bg-blue-50 text-blue-700 px-2 py-0.5 font-bold uppercase tracking-widest">
                        {featured.trades[0]?.replace(/-/g, ' ')}
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-md bg-orange-50 text-[#F97316] px-2 py-0.5 font-bold uppercase tracking-widest">
                        {featured.category.replace('-', ' ')}
                      </span>
                      <span className="inline-flex items-center gap-1 text-slate-500 font-semibold">
                        <Clock className="w-3 h-3" /> {featured.estimated_minutes} minutes
                      </span>
                    </div>
                    <p className="mt-3 text-sm text-slate-700 leading-relaxed">
                      {featured.objective}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Link to={`/qst/lessons/${featured.slug}`}
                        className="inline-flex items-center gap-1.5 rounded-md bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2 text-sm font-bold shadow-sm"
                        data-testid="qst-featured-view">
                        <Play className="w-3.5 h-3.5" /> Present to Crew
                      </Link>
                      <button className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-100 px-4 py-2 text-sm font-bold">
                        <Headphones className="w-3.5 h-3.5" /> Listen
                      </button>
                      <button className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-100 px-4 py-2 text-sm font-bold">
                        <Download className="w-3.5 h-3.5" /> Download PDF
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Discussion questions preview */}
              <div className="rounded-xl bg-white border border-slate-200 shadow-sm p-6">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-600">Discussion Questions</div>
                <ul className="mt-3 space-y-3 text-sm text-slate-800">
                  <li className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-[#0B2545]/10 text-[#0B2545] text-[10px] font-black grid place-items-center flex-shrink-0">Q</span>
                    How can we verify materials on site before opening pallets?
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-[#0B2545]/10 text-[#0B2545] text-[10px] font-black grid place-items-center flex-shrink-0">Q</span>
                    What are common red flags to watch for on delivery day?
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-[#0B2545]/10 text-[#0B2545] text-[10px] font-black grid place-items-center flex-shrink-0">Q</span>
                    What should you do if materials don't match the submittal?
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ============ TOOLS ============ */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <h2 className="text-2xl sm:text-3xl font-bold text-[#0B2545]">Practical Tools That Prevent Loss</h2>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4 items-stretch">
          {[
            { icon: Calculator, title: "Rework Cost Calculator", desc: "Estimate the true cost of rework on your projects.", to: "/qst/tools/rework-cost", accent: "#F97316" },
            { icon: Calculator, title: "Injury Cost Calculator", desc: "Understand the financial impact of incidents.",     to: "/qst/tools/injury-cost", accent: "#DC2626" },
            { icon: FileText,   title: "Toolbox Talk Builder",  desc: "Create customized talks for your crew.",             to: "/qst/tools/toolbox-builder", accent: "#0B2545" },
            { icon: ListChecks, title: "Material Verification Checklist", desc: "Ensure the right materials are on site, every time.", to: "/qst/tools/checklist", accent: "#059669" },
          ].map((tool) => (
            <Link key={tool.title} to={tool.to}
              className="rounded-xl border border-slate-200 bg-white p-5 hover:shadow-md transition group"
              data-testid={`qst-tool-${tool.title.split(' ')[0].toLowerCase()}`}>
              <div className="w-10 h-10 rounded-lg grid place-items-center" style={{ background: `${tool.accent}18` }}>
                <tool.icon className="w-5 h-5" style={{ color: tool.accent }} />
              </div>
              <div className="mt-3 text-base font-bold text-[#0B2545]">{tool.title}</div>
              <div className="mt-1 text-xs text-slate-600 leading-relaxed">{tool.desc}</div>
              <div className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#F97316]">
                Open tool <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ============ EXPERT PROMISE ============ */}
      <section className="bg-slate-50 py-16" data-testid="qst-experts">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#0B2545]">Construction Expertise First</h2>
          <p className="mt-2 text-slate-700 max-w-3xl">
            Every authoritative lesson is created and reviewed by experienced construction professionals.
          </p>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { name: "James O'Brien", years: 25, role: "Lead Author", initials: "JO" },
              { name: "Maria Delgado", years: 15, role: "Technical Reviewer, PE", initials: "MD" },
              { name: "Marcus Ellis",  years: 30, role: "Field Author", initials: "ME" },
            ].map((e) => (
              <div key={e.name} className="rounded-xl border border-slate-200 bg-white p-5 flex items-start gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#0B2545] to-[#1D4ED8] text-white font-black text-lg grid place-items-center">
                  {e.initials}
                </div>
                <div>
                  <div className="text-sm font-bold text-[#0B2545]">Construction Professional</div>
                  <div className="text-xs text-slate-600 mt-0.5">{e.years}+ years of experience</div>
                  <div className="mt-2 inline-flex items-center gap-1 text-xs text-[#059669] font-bold">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Verified Reviewer
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 max-w-3xl">
            <AiSafetyBadge>
              Every AI answer must cite the approved lesson, its revision date and its author. The library
              is the authority — not the AI.
            </AiSafetyBadge>
          </div>
        </div>
      </section>

      {/* ============ FOR ORGANIZATIONS ============ */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#0B2545]">Built for Crews, Contractors and Organizations</h2>
          <Link to="/qst/for-organizations"
            className="inline-flex items-center gap-1.5 rounded-md bg-[#1D4ED8] hover:bg-[#1740b3] text-white px-4 py-2 text-sm font-bold">
            Explore Organization Solutions <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-3">
          {[
            { icon: HardHat, title: "Foremen", desc: "Deliver consistent, high-quality training at the jobsite." },
            { icon: Briefcase, title: "Trade Contractors", desc: "Keep crews trained and projects on track." },
            { icon: Building2, title: "General Contractors", desc: "Standardize training across all projects." },
            { icon: Users, title: "Associations & Insurers", desc: "Support safer, stronger construction communities." },
          ].map((a) => (
            <div key={a.title} className="rounded-xl border border-slate-200 bg-white p-4 flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-[#0B2545]/8 grid place-items-center flex-shrink-0" style={{ background: "#0B254515" }}>
                <a.icon className="w-4.5 h-4.5 text-[#0B2545]" />
              </div>
              <div>
                <div className="text-sm font-bold text-[#0B2545]">{a.title}</div>
                <div className="mt-0.5 text-xs text-slate-600 leading-relaxed">{a.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="bg-[#0B2545] py-16 relative overflow-hidden" data-testid="qst-final-cta">
        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
          backgroundImage: "repeating-linear-gradient(45deg, #F97316 0 2px, transparent 2px 16px)"
        }} />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-black text-white leading-tight">
            Give Every Crew the Right Lesson Before Work Begins.
          </h2>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Link to="/qst/signup"
              className="inline-flex items-center gap-2 rounded-md bg-[#F97316] hover:bg-[#EA580C] text-white px-8 py-3.5 font-black shadow-xl transition"
              data-testid="qst-final-start-free">
              Start Free <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/qst/contact?intent=demo"
              className="inline-flex items-center gap-2 rounded-md bg-white/10 hover:bg-white/20 border-2 border-white text-white px-8 py-3.5 font-black transition"
              data-testid="qst-final-demo">
              Request a Demonstration
            </Link>
          </div>
        </div>
      </section>
    </QstLayout>
  );
}
